import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { CountryCode } from 'libphonenumber-js';
import { FormEvent, useId, useMemo, useState } from 'react';
import {
  ValidationError,
  useCreateRestaurantCompanyMutation,
  parsePhoneNumber,
  asYouTypePhoneNumber,
  phoneNumberInputPlaceholder,
  useAuthUserRestaurant,
} from '@durma-soft/gros-sdk';

import { Label } from '@/components/shared/shadcn-ui/label';
import { Input } from '@/components/shared/shadcn-ui/input';
import { InputAddon } from '@/components/shared/input-addon';
import { Button } from '@/components/shared/shadcn-ui/button';
import { DialogFooter } from '@/components/shared/shadcn-ui/dialog';
import { CustomFileInput } from '@/components/shared/custom-file-input';
import { SelectCountryCallingCode } from '@/components/shared/select-country-code';

import { CreateRestaurantCompanyFormData } from '@/types/companies';

interface CreateRestaurantCompanyFormProps {
  closeModal: () => unknown;
}

const initialFormData: CreateRestaurantCompanyFormData = {
  name: '',
  logo: null,
  phone_number: '',
  username_suffix: '',
};

export const CreateRestaurantCompanyForm = ({
  closeModal,
}: CreateRestaurantCompanyFormProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(initialFormData);
  const [countryCode, setCountryCode] = useState<CountryCode>(
    import.meta.env.VITE_DEFAULT_COUNTRY_CODE || 'BA',
  );

  const uid = useId();
  const restaurant = useAuthUserRestaurant();
  const createRestaurantCompany = useCreateRestaurantCompanyMutation();

  const isValid = useMemo(() => {
    return (
      formData.name.trim().length > 0 &&
      formData.username_suffix.trim().length > 0
    );
  }, [formData]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isValid) return;

    try {
      await createRestaurantCompany.mutateAsync({
        restaurant_id: restaurant.id,
        name: formData.name,
        logo: formData.logo,
        phone_number:
          parsePhoneNumber(formData.phone_number, countryCode)?.format(
            'E.164',
          ) || null,
        username_suffix: formData.username_suffix.toLowerCase(),
      });
      closeModal();
      setFormData(initialFormData);
      toast.success(t('company.create-success-msg'));
    } catch (error) {
      if (error instanceof ValidationError) {
        return toast.error(error.message);
      }
      toast.error(t('company.create-error.msg'));
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid gap-4 py-4">
        {/* Name */}
        <div className="flex flex-col gap-2">
          <Label htmlFor={uid + '-name'}>{t('common.name')}</Label>
          <div className="flex items-stretch w-full">
            <Input
              required
              id={uid + '-name'}
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder={t('common.name-placeholder')}
            />
          </div>
        </div>
        {/* Username suffix */}
        <div className="flex flex-col gap-2">
          <Label htmlFor={uid + '-username-suffix'}>
            {t('company.suffix')}
          </Label>
          <div className="flex items-stretch">
            <InputAddon position="left">@</InputAddon>
            <Input
              required
              className="flex-1 border-l-0 rounded-tl-none rounded-bl-none"
              id={uid + '-username-suffix'}
              value={formData.username_suffix}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  username_suffix: e.target.value,
                })
              }
              placeholder={t('company.suffix-placeholder')}
            />
          </div>
        </div>
        {/* Phone number */}
        <div className="flex flex-col gap-2">
          <Label htmlFor={uid + '-phone-number'}>
            {t('company.phone-number')}
          </Label>
          <div className="flex items-stretch">
            <InputAddon position="left">
              <SelectCountryCallingCode
                value={countryCode}
                onChange={(e) => {
                  setCountryCode(e.target.value as CountryCode);
                  setFormData({
                    ...formData,
                    phone_number: '',
                  });
                }}
              />
            </InputAddon>
            <Input
              className="flex-1 border-l-0 rounded-tl-none rounded-bl-none"
              id={uid + '-phone-number'}
              value={formData.phone_number}
              placeholder={phoneNumberInputPlaceholder[countryCode]}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  phone_number: asYouTypePhoneNumber(
                    e.target.value,
                    countryCode,
                  ),
                })
              }
            />
          </div>
        </div>
        {/* Logo */}
        <div className="flex flex-col gap-2">
          <CustomFileInput
            label={t('common.add-logo')}
            handleFile={(logo: File) => setFormData({ ...formData, logo })}
            hasDeleteButton={Boolean(formData.logo)}
            onDelete={() => setFormData({ ...formData, logo: null })}
          />
          <div>
            {formData.logo && (
              <img
                src={URL.createObjectURL(formData.logo)}
                height="128"
                width="128"
                className="object-contain object-center w-32 h-32 rounded-md"
              />
            )}
          </div>
        </div>
      </div>
      <DialogFooter className="mt-2">
        <Button
          type="submit"
          disabled={!isValid}
          isLoading={createRestaurantCompany.isPending}
        >
          {t('company.create')}
        </Button>
      </DialogFooter>
    </form>
  );
};
